import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './HalloweenOverlay.css';

/** Показывается до 2 ноября. Раньше — убрать импорт в App.tsx. */
const SHOW_UNTIL = new Date(2026, 10, 2).getTime();

/** Паутина в углу: CC0, Pixabay / Wikimedia, File:Corner cobwebs.png. */
function Web({ className }: { className: string }) {
  return <img className={`hween-web ${className}`} src="/seasonal/spider-web.png?v=2" alt="" />;
}

const FOXY_URL = '/seasonal/foxy.webp';
const FOXY_MS = 620;
let foxyB64: Promise<string> | null = null;

function bytesToBase64(buf: ArrayBuffer) {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 8192) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + 8192)));
  }
  return btoa(binary);
}

function loadFoxy() {
  if (!foxyB64) {
    foxyB64 = fetch(FOXY_URL)
      .then((res) => {
        if (!res.ok) throw new Error('foxy');
        return res.arrayBuffer();
      })
      .then(bytesToBase64)
      .catch((err) => {
        foxyB64 = null;
        throw err;
      });
  }
  return foxyB64;
}

/** data: разрешён CSP (blob: нет). Уникальный параметр — чтобы ролик каждый раз шёл с первого кадра. */
function FoxyScare({ onDone }: { onDone: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  const onDoneRef = useRef(onDone);
  const timerRef = useRef(0);
  onDoneRef.current = onDone;

  useEffect(() => {
    let dead = false;
    loadFoxy()
      .then((b64) => {
        if (dead) return;
        setSrc(`data:image/webp;r=${Date.now()};base64,${b64}`);
      })
      .catch(() => {
        if (!dead) onDoneRef.current();
      });
    return () => {
      dead = true;
      window.clearTimeout(timerRef.current);
    };
  }, []);

  return createPortal(
    <div className="hween-scare" onClick={onDone} role="presentation">
      {src && (
        <img
          src={src}
          alt=""
          onLoad={() => {
            if (timerRef.current) return;
            timerRef.current = window.setTimeout(() => onDoneRef.current(), FOXY_MS);
          }}
          onError={() => onDoneRef.current()}
        />
      )}
    </div>,
    document.body,
  );
}

export default function HalloweenOverlay() {
  const on = Date.now() < SHOW_UNTIL;
  const runners = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!on) return undefined;
    document.body.classList.add('halloween');
    loadFoxy().catch(() => undefined);
    return () => document.body.classList.remove('halloween');
  }, [on]);

  useEffect(() => {
    if (!on) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    const host = runners.current;
    if (!host) return undefined;
    let clicks = 0;
    let alive = 0;

    const onEnd = (event: AnimationEvent) => {
      const bug = event.target;
      if (!(bug instanceof HTMLElement) || !bug.classList.contains('hween-run')) return;
      alive -= 1;
      bug.remove();
    };

    const onClick = (event: MouseEvent) => {
      const el = event.target;
      if (!(el instanceof Element)) return;
      if (el.closest('.hween-spider')) return;
      if (!el.closest('button, a, [role="button"]')) return;
      clicks += 1;
      if (clicks % 3 !== 0 || alive > 10) return;

      const n = 3 + Math.floor(Math.random() * 3);
      for (let i = 0; i < n; i += 1) {
        const bug = document.createElement('span');
        bug.className = 'hween-run';
        const body = document.createElement('span');
        body.textContent = '🕷️';
        body.className = 'hween-crawl';
        bug.appendChild(body);
        const angle = (Math.PI * 2 * i) / n + (Math.random() - 0.5) * 0.7;
        const dist = 90 + Math.random() * 130;
        bug.style.left = `${event.clientX}px`;
        bug.style.top = `${event.clientY}px`;
        bug.style.fontSize = `${14 + Math.random() * 8}px`;
        bug.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
        bug.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
        bug.style.animationDuration = `${0.85 + Math.random() * 0.45}s`;
        alive += 1;
        host.appendChild(bug);
      }
    };

    host.addEventListener('animationend', onEnd);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('click', onClick, true);
      host.removeEventListener('animationend', onEnd);
      host.replaceChildren();
    };
  }, [on]);

  const [scare, setScare] = useState(false);
  const closeScare = useCallback(() => setScare(false), []);

  if (!on) return null;

  const runnersLayer = (
    <div className="hween-runners" ref={runners} />
  );

  return (
    <>
      <div className="hween">
        <div aria-hidden="true">
          <Web className="hween-web-tl" />
          <Web className="hween-web-tr" />
          <Web className="hween-web-br" />
          <span className="hween-bat hween-bat-a">
            <span className="hween-flap">🦇</span>
          </span>
          <span className="hween-bat hween-bat-b">
            <span className="hween-flap">🦇</span>
          </span>
        </div>
        <button
          type="button"
          className="hween-spider"
          aria-label="Паук"
          onClick={() => setScare(true)}
        >
          🕷️
        </button>
      </div>
      {createPortal(runnersLayer, document.body)}
      {scare && <FoxyScare onDone={closeScare} />}
    </>
  );
}
